import { useEffect } from 'react';

import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { Box, Grid, InputAdornment } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { useParams, useMatch } from 'react-router-dom';

import {
  SelectFormField,
  DatePickerFormField,
  TextFormField,
  AutocompleteFormField,
} from 'components/FormFields';
import { PickerTypes } from 'components/FormFields/DatePickerFormField';
import strings, { url } from 'constants/index';
import { EnumValueNames, useEnum } from 'contexts/EnumValuesContext';
import { useCompetitorsType } from 'hooks/context';
import { normalizeEnumValues } from 'utils/normalizers.utils';

interface CompetitorFiltersFormProps {
  onChange: (values: Array<PaginationFilter>) => void;
  genderType?: string;
}
const CompetitorFilters = ({ onChange, genderType }: CompetitorFiltersFormProps) => {
  const [competitorsType, setCompetitorsType] = useCompetitorsType();
  const genderTypes = useEnum(EnumValueNames.Gender);
  const isSeason = !!useMatch(`${url.base_seasons}/:seasonId`);
  const isRound = !!useMatch(`${url.base_rounds}/:roundId`);
  const { id } = useParams<string>();

  const hasDateOfBirthFilter =
    (competitorsType === strings.person || competitorsType === strings.player) && !isRound;
  const competitorTypesEnum = useEnum(EnumValueNames.Competitors);
  const competitorTypesForDropdown = ['DoublesPartnership', 'Team', 'Player'];
  const competitorTypes = Object.values(competitorTypesEnum).filter(competitorType =>
    competitorTypesForDropdown.includes(competitorType.name),
  );
  const {
    getValues,
    setValue,
    control,
    formState: { dirtyFields },
  } = useForm({
    mode: 'onChange',
  });
  const localityName = useWatch({ name: 'locality.name', control });
  const dateOfBirth = useWatch({ name: 'dateOfBirth', control });
  const gender = useWatch({ name: 'genderType', control });
  const nameOrId = useWatch({ name: 'nameOrId', control });
  const competitorType = useWatch({ name: 'competitorType', control });
  const gridWidth = isSeason ? (hasDateOfBirthFilter ? 3 : 4) : hasDateOfBirthFilter ? 4 : 6;
  useEffect(() => {
    if (isSeason) {
      const savedCompetitorType = parseInt(localStorage.getItem(id));
      if (typeof savedCompetitorType === 'number' && isFinite(savedCompetitorType)) {
        setCompetitorsType(competitorTypes.find(ct => ct.value === savedCompetitorType).name);
        setValue('competitorType', savedCompetitorType);
      }
    }
  });
  useEffect(() => {
    if (competitorType !== undefined && competitorTypes) {
      const value = competitorTypes.find(ct => ct.value === competitorType).value;
      localStorage.setItem(id, value.toString());
      setValue('competitorType', competitorTypes.find(ct => ct.value === competitorType).value);
      setCompetitorsType(competitorTypes.find(ct => ct.value === competitorType).name);
    }
  }, [competitorType, competitorTypes, id, setCompetitorsType, setValue]);
  useEffect(() => {
    const changedFields = [];
    const columnIdPrefix = competitorsType === strings.player ? 'person.' : '';
    Object.keys(dirtyFields).forEach(field => {
      const value = getValues(field);
      if (field === 'locality' && value?.name) {
        changedFields.push({
          columnId: `${columnIdPrefix}locality.name`,
          value: value.name,
        });
      } else if (field === 'nameOrId' && value) {
        if (/^\d+$/.test(nameOrId)) {
          changedFields.push({ columnId: 'id', value });
        } else {
          changedFields.push({
            columnId: competitorsType === strings.person ? 'fullName' : 'name',
            value,
            condition: 'contains',
          });
        }
      } else if (value && field !== 'competitorType') {
        changedFields.push({ columnId: `${columnIdPrefix}${field}`, value });
      }
    });
    const timeoutHandler = setTimeout(() => onChange(changedFields), 250);
    return () => clearTimeout(timeoutHandler);
  }, [
    onChange,
    gender,
    getValues,
    dirtyFields,
    dateOfBirth,
    localityName,
    nameOrId,
    competitorsType,
    setCompetitorsType,
  ]);

  return (
    <Box p={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={gridWidth}>
              <AutocompleteFormField
                textFieldProps={{
                  variant: 'outlined',
                  size: 'small',
                }}
                control={control}
                label={strings.locality}
                name="locality"
                dataSource={url.locality_datasource}
              />
            </Grid>
            {hasDateOfBirthFilter && (
              <Grid item xs={12} sm={isSeason ? 3 : 4}>
                <DatePickerFormField
                  inputVariant="outlined"
                  pickerType={PickerTypes.Date}
                  disableFuture={true}
                  control={control}
                  label={strings.date_of_birth}
                  name="dateOfBirth"
                  clearable
                  size="small"
                />
              </Grid>
            )}
            {(genderType === 'Mixed' || typeof genderType === 'undefined') && (
              <Grid item xs={12} sm={gridWidth}>
                <SelectFormField
                  variant="outlined"
                  size="small"
                  control={control}
                  name="genderType"
                  label={strings.gender}
                  values={normalizeEnumValues(genderTypes)}
                  allowEmpty
                />
              </Grid>
            )}
            {isSeason && (
              <Grid item xs={12} sm={gridWidth}>
                <SelectFormField
                  variant="outlined"
                  size="small"
                  control={control}
                  name="competitorType"
                  label={strings.competitor_type}
                  values={normalizeEnumValues(competitorTypes)}
                  allowEmpty={false}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextFormField
            size="small"
            variant="outlined"
            // sx={{ background: '#F2F2F5' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
            control={control}
            name="nameOrId"
            placeholder={strings.search_by_id_or_name}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompetitorFilters;
